import { IconButton } from '@droplet-tech-code/core-elements/module/ui/Button';
import {
  FormBuilder,
  FormHandler,
} from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { showToast } from '@droplet-tech-code/core-elements/module/ui/Toast';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import {
  ManualKoalaQuoteDetails,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { useEffect, useRef } from 'react';
import { companyConfigApi } from '~/screens/Configs/Company/CompanyConfigs.api';
import { portLocationApi } from '~/screens/Locations/PortLocations/PortLocations.api';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { useCreateKoalaQuoteStore } from '~/screens/Support/Requests/utils/supportRequest.store';
import { vesselApi } from '~/screens/Vessels/Vessels.api';
import { translate } from '~/utils/translation.utils';

export const GetKoalaQuoteKey = 'GetKoalaQuoteModalKey';
export const GetKoalaQuoteModal = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const supportId = supportRequestInfo._id;
  const formRef = useRef(new FormHandler());

  const { closeModal } = useModal();

  const { data } = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId,
  });
  const { data: portData } = portLocationApi.useGetPortLocationListQuery();
  const { data: vesselData } = vesselApi.useGetVesselListQuery();
  const { data: companyData } = companyConfigApi.useGetCompanyConfigListQuery();

  const initialiseStore = useCreateKoalaQuoteStore(
    (state) => state.initialiseKoalaQuoteForm,
  );

  const [onConfirmGetKoala, { isLoading: isConfirming }] =
    supportRequestActionsApi.useGetKoalaQuoteMutation();

  const onConfirm = (details: ManualKoalaQuoteDetails) => {
    // const { data } = supportRequestApi.useGetSupportRequestQuery(supportId);
    return onConfirmGetKoala({
      supportId,
      check: check,
      koalaDetails: details,
      bookingId: supportRequestInfo.bookingId!,
    });
  };

  useEffect(() => {
    const hasAllData =
      data?.booking && portData && vesselData && companyData?.ferryCompanies;
    if (hasAllData) {
      const vessels = vesselData.map((vessel) => {
        return {
          label: vessel.title,
          value: vessel.id,
        };
      });
      const ports = portData.map((port) => {
        return {
          label: port.title,
          value: port.id,
        };
      });
      const companies = companyData.ferryCompanies.map((company) => {
        return {
          label: company.title,
          value: company.id,
        };
      });

      initialiseStore(data.booking, ports, vessels, companies);
    }
  }, [data, portData, vesselData, companyData]);

  const initialFormData = useCreateKoalaQuoteStore((state) => state.koalaQuoteForm);
  const addPassenger = useCreateKoalaQuoteStore((state) => state.addPassengerToForm);

  return (
    <Modal.Container w={1500}>
      <Modal.Header
        title={translate('supportRequests.newKoalaQuote')}
        subtitle={translate('supportRequests.newKoalaQuoteDescription')}
        divider
        children={<IconButton icon="plus-circle" onPress={addPassenger} />}
        onPress={() => {
          closeModal(GetKoalaQuoteKey);
        }}
      />
      {!initialFormData ? (
        <LoadingIndicator />
      ) : (
        <Modal.Body p="0">
          <FormBuilder data={initialFormData} form={formRef.current} />
        </Modal.Body>
      )}
      <Modal.Footer
        divider
        isLoading={isConfirming}
        onConfirm={async () => {
          //
          const data = formRef.current.getValue();
          if (!data.valid) {
            showToast('invalid data', { type: 'error' });
            return;
          }
          const value = data.value as { [key: string]: any };
          const details = mapFormDataToRequestDetails(value);

          const response = await onConfirm(details);

          handleResponse({
            response,
            onSuccess: () => {
              closeModal(GetKoalaQuoteKey);
            },
            onError: () => {
              // closeModal(GetKoalaQuoteKey);
            },
          });
        }}
        onCancel={() => {
          closeModal(GetKoalaQuoteKey);
        }}
      />
    </Modal.Container>
  );
};

export const useGetKoalaQuote = () => {
  const { openModal } = useModal();

  return {
    open: (supportRequestInfo: SupportRequestBackendClient, check: string) => {
      openModal(
        <GetKoalaQuoteModal supportRequestInfo={supportRequestInfo} check={check} />,
        {
          key: GetKoalaQuoteKey,
          type: 'action-sheet-full',
        },
      );
    },
  };
};

function mapFormDataToRequestDetails(values: {
  [key: string]: any;
}): ManualKoalaQuoteDetails {
  const request = {
    booking: {},
    tripDetails: {},
  } as ManualKoalaQuoteDetails;
  const passengers = {} as { [id: string]: ManualKoalaQuoteDetails['passengers'][0] };
  for (const [entry, value] of Object.entries(values)) {
    const [key, type, id] = entry.split('-');
    switch (type) {
      case 'booking':
        request.booking = {
          value: Math.round(value * 100),
        };
        break;
      case 'trip': {
        const oldTrip = request?.tripDetails?.[id] || {};
        request.tripDetails[id] = {
          ...oldTrip,
          [key]: value,
        };
        break;
      }
      case 'passenger': {
        const oldPassenger = passengers?.[id] || {};
        passengers[id] = {
          ...oldPassenger,
          [key]: value,
        };
        break;
      }
      default:
        break;
    }
  }
  request.passengers = Object.values(passengers);
  return request;
}
